import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
// 1. Import Trash2 icon
import { Bell, MailCheck, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const fetchNotifications = async () => {
    const { data } = await api.get('/notifications?limit=10');
    return data;
};

const Notifications = ({ isOpen: isSidebarOpen }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: fetchNotifications,
        refetchInterval: 60000,
    });

    const markReadMutation = useMutation({
        mutationFn: (ids) => api.post('/notifications/mark-read', { ids }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    // 2. Add the new mutation for deleting notifications
    const deleteMutation = useMutation({
        mutationFn: (notificationId) => api.delete(`/notifications/${notificationId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleMarkAsRead = (notification) => {
        if (!notification.isRead) {
            markReadMutation.mutate([notification._id]);
        }
        setIsDropdownOpen(false);
    };

    const handleMarkAllAsRead = () => {
        markReadMutation.mutate([]);
    };
    
    // 3. Create the event handler for the delete button
    const handleDelete = (notificationId, event) => {
        event.stopPropagation();
        event.preventDefault();
        deleteMutation.mutate(notificationId);
    };

    const notifications = data?.notifications || [];
    const unreadCount = data?.unreadCount || 0;

    return (
        <div ref={dropdownRef} className="relative">
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`group flex items-center w-full px-2 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-gray-700 ${!isSidebarOpen && 'justify-center'}`}
                aria-label="Toggle notifications"
            >
                <div className="indicator">
                    <Bell className={`flex-shrink-0 h-6 w-6 ${isSidebarOpen ? 'mr-3' : 'mr-0'}`} />
                    {unreadCount > 0 && (
                        <span className="badge badge-sm badge-error indicator-item">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    )}
                </div>
                <span className={`${isSidebarOpen ? 'block' : 'hidden'}`}>
                    Notifications
                </span>
            </button>

            {isDropdownOpen && (
                <div className={`absolute w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 z-50 ${isSidebarOpen ? 'left-0 bottom-full mb-2' : 'left-full top-0 ml-2'}`}>
                    <div className="p-3 border-b dark:border-gray-700">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold text-lg text-gray-800 dark:text-white">Notifications</h3>
                            <button onClick={handleMarkAllAsRead} disabled={unreadCount === 0} className="text-sm text-blue-500 hover:underline disabled:opacity-50">
                                Mark all as read
                            </button>
                        </div>
                    </div>
                    
                    {isLoading ? (
                        <p className="p-4 text-center text-gray-500">Loading...</p>
                    ) : notifications.length > 0 ? (
                        <ul className="max-h-96 overflow-y-auto">
                            {notifications.map(n => (
                                <li key={n._id} className={`group border-l-4 relative ${!n.isRead ? 'border-blue-500 bg-gray-50 dark:bg-gray-700/50' : 'border-transparent'}`}>
                                    <Link to={n.link} onClick={() => handleMarkAsRead(n)} className="block p-3 hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <p className="font-semibold text-sm text-gray-800 dark:text-gray-100">{n.title}</p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">{n.body}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                            {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                                        </p>
                                    </Link>
                                    {/* 4. Add the delete button that appears on hover */}
                                    <button
                                        onClick={(e) => handleDelete(n._id, e)}
                                        className="absolute top-1/2 right-2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/50 opacity-0 group-hover:opacity-100 transition-opacity"
                                        aria-label="Delete notification"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <MailCheck className="mx-auto h-12 w-12 text-gray-400" />
                            <p className="mt-2 text-sm">You're all caught up!</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Notifications;